import React, { FC, useEffect, useRef, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Category } from 'shared/types/product/Category';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import ArchiProSnippet from 'components/headless/ArchiProSnippet';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { minitablet } from 'helpers/utils/screensizes';
import AdditionalInfo from './components/additional-info';
import CleanFlushIcons, { isCleanFlushEnabled } from './components/clean-flush-icons';
import ProductInformation from './components/product-information';
import { UIProduct } from './types';
import { HomeIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useParams, useSearchParams } from 'next/navigation';
import ProductSpecification from './components/product-specification';
import { GENERIC_ATTRIBUTES } from 'helpers/constants/attributes';
import WhiteOverlay from 'components/commercetools-ui/atoms/overlay/white-overlay';
import BundleProducts from './components/bundleProducts';
import SpareParts from './components/spare-parts';
import { getCategoryLink } from './helpers/getCategoryURL';
import SidebarPDP from './components/sidebar';
import { localeMapping } from 'helpers/constants/localeMapping';
import Spinner from '../../spinner';
import { CT_STOREKEY } from 'helpers/constants/envVariables';
import { fetchBundleProducts } from 'helpers/next-services';
import { v4 as uuidv4 } from 'uuid';
import { Tiempos } from 'fonts';
import { PRODUCT_RANGES } from 'helpers/constants/collection-names';
import { sessionStorageKeys } from 'helpers/constants/sessionStorageKeys';

export interface ProductDetailsProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  // Server-seeded props (SSR). Bundles/spares for the initial variant + category tree.
  initialBundleData?: any[];
  initialSparePartsData?: any[];
  categories?: Category[];
}

const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  variant,
  url,
  onChangeVariant,
  inModalVersion,
  setIsOpen,
  onAddToCart,
  initialBundleData = [],
  initialSparePartsData = [],
  categories = [],
}) => {
  const [loading, setLoading] = useState(true);
  const [isSpinnerVisible, setSpinner] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
  const [isTabletSize] = useMediaQuery(minitablet);
  // Seed the category synchronously from the server-provided categories prop so the
  // breadcrumb renders in the SSR HTML (was previously set in an effect off client SWR).
  const findCategory = (cats: Category[]) =>
    cats?.find((cat) =>
      cat.subCategories?.find((sub) => sub.categoryId === product?.categories?.[0]?.categoryId),
    );
  const [category, setCategory] = useState<Category | undefined>(() => findCategory(categories));
  const [inventoryData, setInventoryData] = useState();

  const searchParams = useSearchParams();
  let categoryLink: string | null | undefined = '';
  let collectionLink: string | null | undefined = '';
  const categoryURLHelper = getCategoryLink(product?.categories?.[0]?.categoryId?.toString());
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  useEffect(() => {
    if (product && categories && !category) {
      setCategory(findCategory(categories));
    }
  }, [categories, category, product, product?.categories]);

  // Seed from server-fetched bundles/spares so they render in the SSR HTML.
  const [bundleAndSparesSection, setBundleAndSparesSection] = useState<{
    bundleData: any[];
    sparePartsData: any[];
    sidebarKey: string;
  }>(() => ({
    bundleData: initialBundleData ?? [],
    sparePartsData: initialSparePartsData ?? [],
    sidebarKey: uuidv4()
  }));

  const fetchBundleData = async () => {
    const bundleReferences = variant?.attributes && 
    variant.attributes['item-attributes']?.find((attribute: { name: string; value?: string }) => attribute.name === 'bundle-references')?.value;
    if(bundleReferences && bundleReferences.length>0){
      const requestData = {
        variant: bundleReferences,
        storeKey: CT_STOREKEY + locale,
      };

      try {
        const responseBody = await fetchBundleProducts({ ...requestData, locale: locale as string });
        if (responseBody && responseBody.length > 0) {
          return responseBody;
        } else {
          return [];
        }
      } catch (error) {
        console.error('Bundle Data Fetch Error:', error);
        return [];
      }
    }else{
      return []
    }
  };

  const fetchSpareParts = async () => {
    const sparePartsReferences = variant.attributes &&
      variant.attributes['item-attributes']?.find((attribute: { name: string; value?: string }) => attribute.name === 'spare-references')?.value;
    if(sparePartsReferences && sparePartsReferences.length>0){      
      const requestData = {
        variant: sparePartsReferences,
        storeKey: CT_STOREKEY + locale
      };

      try {
        const responseBody = await fetchBundleProducts({ ...requestData, locale: locale as string });
        if (responseBody && responseBody.length > 0) {
          return responseBody;
        } else {
          return [];
        }
      } catch (error) {
        console.error('Spare Parts Data Fetch Error:', error);
        return [];
      }
    }else{
      return [];
    }
  };

  const updateBundleAndSparesSection = async () => {
    setBundleAndSparesSection({
      bundleData: (await fetchBundleData()),
      sparePartsData: (await fetchSpareParts()),
      sidebarKey: uuidv4(),
    });
  }

  // The initial variant's bundles/spares are seeded server-side, so skip the first
  // run to avoid a redundant refetch and a full-screen loader flash on hydration.
  // On subsequent (client-side) variant changes this re-fetches without a reload.
  const isFirstBundleRun = useRef(true);
  useEffect(() => {
    if (isFirstBundleRun.current) {
      isFirstBundleRun.current = false;
      return;
    }
    setIsPageLoading(true);
    updateBundleAndSparesSection();
  }, [variant]);
   useEffect(() => {
    if (product && variant && !loading) {
      setIsPageLoading(false);
    }
  }, [product, variant, loading]);
//className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}
const generateBreadcrumbs = () => {
  if(!categoryLink) categoryLink = searchParams.get('category') as string;
  if(!collectionLink) collectionLink = searchParams.get('collection') as string;
  if(!categoryLink && !collectionLink){
    if (typeof window !== 'undefined') {
      categoryLink = sessionStorage.getItem(sessionStorageKeys.categoryLink);
      collectionLink = sessionStorage.getItem(sessionStorageKeys.collectionLink);
    }
  }
  if(collectionLink && collectionLink.split('/')[1].toLowerCase() == 'our-technologies'){
    const isaurajet = collectionLink?.includes('aurajet-technology');
    const issatinjet = collectionLink?.includes('satinjet-technology');
    const isvjet = collectionLink?.includes('vjet-technology');
    const isairstream = collectionLink?.includes('airstream-technology');

    return [
        <Link key='1' link='/our-technologies/'>
          <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
            Our Technologies
          </span>
        </Link>,
        <Link key='2' link={collectionLink}>
          <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
          {
            isaurajet ? 'Aurajet Collection' : 
            issatinjet ? 'Satinjet Collection' : 
            isvjet ? 'V-Jet Collection' : 
            isairstream ? 'Airstream Collection' : 
            ''
          }          
          </span>
        </Link>
    ];}
  if(collectionLink && PRODUCT_RANGES.some(range => collectionLink && range.toLowerCase() === collectionLink?.split('/')[2].toLowerCase())){
    return [
        <span key='1' style={{ cursor: 'default' }} className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
          Collection
        </span>,
        <Link key='2' link={collectionLink}>
          <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
          {collectionLink.split('/')[2]}
          </span>
        </Link>
    ];}
  if(categoryLink &&
    ['flexispray', 'showers','tapware & accessories'].includes(categoryLink.split('/')[1].toLowerCase().replaceAll('-',' ').replaceAll('and','&'))
  ){
    if(categoryLink.split('/')[2]){    
    return [
      <Link key='1' link={`${categoryLink.split('/').splice(0,2).join('/')}/`}>
      <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
        {categoryLink.split('/')[1].replaceAll('-',' ').replaceAll('and','&')}
      </span>
      </Link>,
      <Link key='2' link={categoryLink}>
        <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
        {categoryLink.split('/')[2].replaceAll('-',' ')}
        </span>
      </Link>
    ];
    }else{
    return [
        <Link key='1' link={`${categoryLink.split('/').splice(0,2).join('/')}/`}>
        <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
          {categoryLink.split('/')[1].replaceAll('-',' ').replaceAll('and','&')}
        </span>
        </Link>
    ];}}
    //categoryURLHelper
    return [
      <Link key='2' link={categoryURLHelper}>
      <span className={`text-14 font-normal leading-4 text-[#666] capitalize ${Tiempos.className}`}>
      {categoryURLHelper.replaceAll('/','').replaceAll('-',' ').replaceAll('and','&')}
      </span>
      </Link>
    ];
  }
  const sku = variant?.attributes?.['product-code'] || '';
  const color = variant?.attributes?.colour;
  return (
    <>
      {locale === 'nz' && <ArchiProSnippet />}
      {isSpinnerVisible && <Spinner />}
       {isPageLoading && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white bg-opacity-95">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading product...</p>
        </div>
      )}
        <div className="relative left-full z-[20] w-screen -translate-x-full lg:grid lg:grid-cols-12 ">
          {loading && <WhiteOverlay />}
          {category && (
          <Breadcrumb
            Separator={<ChevronRightIcon className="mt-4 md:w-14 lg:w-16 xl:w-14 2xl:w-16" />}
            listClassName="!gap-x-16 !px-0"
            className="absolute left-24 col-span-12 mb-32 mt-84 hidden px-0 md:left-28 md:mt-20 md:block md:px-0 lg:left-48 lg:px-16 xl:left-60 xl:px-16 2xl:left-80 2xl:px-16 3xl:left-80"
          >
            <Link link={'/'}>
              <HomeIcon className="mt-8 w-20" />
            </Link>
            {generateBreadcrumbs()}
          </Breadcrumb>
        )}
        <div className="!z-[50] flex flex-col lg:col-span-9 lg:col-start-4">
          <div className="!z-[50] left-24 mt-32 px-24 md:left-28 md:px-28 lg:absolute lg:left-48 lg:mt-[5rem] lg:px-0 xl:left-60 xl:mt-[3.5rem] xl:pr-60 2xl:left-80 2xl:mt-[4rem] 2xl:pr-80">
            <ProductInformation
              product={product}
              variant={variant}
              onChangeVariant={onChangeVariant}
              inModalVersion={inModalVersion}
            />
          </div>
          <div className="hidden lg:block !w-fit left-24 mt-32 px-24 md:left-28 md:px-28 lg:absolute lg:left-64 lg:mt-[14.75rem] lg:px-0 xl:left-76 xl:mt-[12.5rem] xl:pr-60 2xl:left-96 2xl:mt-[16rem] 3xl:mt-[14rem] 2xl:pr-80">
          <SidebarPDP key={bundleAndSparesSection.sidebarKey} bundleData={bundleAndSparesSection.bundleData} sparePartsData={bundleAndSparesSection.sparePartsData}/>
        </div>
          <div className="mt-[1rem] lg:mt-[15.5rem] xl:mt-[14rem] 2xl:mt-[16rem]">
            <div className="px-0 md:px-0 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pl-0 2xl:pr-80">
              <Gallery images={variant?.images ?? []} setLoading={setLoading} sku={sku} color={color} />
            </div>
            {isCleanFlushEnabled(variant?.attributes) && (
              <div className="lg:hidden px-24 mt-24 md:px-28">
                <CleanFlushIcons />
              </div>
            )}
            <div className="lg:hidden !w-fit left-24 mt-48 px-24 md:left-28 md:px-28">
            <SidebarPDP key={bundleAndSparesSection.sidebarKey} bundleData={bundleAndSparesSection.bundleData} sparePartsData={bundleAndSparesSection.sparePartsData}/>
        </div>
            {!inModalVersion && (
              <div id='product-overview-scroll' className="gap-y-34 pr-24 md:px-28 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pr-80">
                <AdditionalInfo
                  productspec={variant?.attributes}
                  features={
                    variant?.attributes &&
                    variant?.attributes[GENERIC_ATTRIBUTES].filter((att: { name: string }) => att.name === 'features')[0]
                      .value?.[`${localeKey}`]
                  }
                  description={product?.description ?? ''}
                />
              </div>
            )}
          </div>
          <ProductSpecification setSpinner={setSpinner} product={product} variant={variant} />
          <div>
            {bundleAndSparesSection.bundleData && bundleAndSparesSection.bundleData.length > 0 &&  (
              <BundleProducts bundleData={bundleAndSparesSection.bundleData} />
            )}
          </div>
          <div className="">
            {bundleAndSparesSection.sparePartsData && bundleAndSparesSection.sparePartsData.length > 0 && (
              <SpareParts sparePartsData={bundleAndSparesSection.sparePartsData} />
            )}
          </div>
        </div>
        </div>
      </>
  );
};

export default ProductDetails;
